import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

/** Поля вокруг книги, чтобы разворот не упирался в края сцены. */
const FIT_PADDING_PX = 24;
const FLIP_DURATION_MS = 680;
const FLIP_EASING = 'cubic-bezier(0.42, 0, 0.24, 1)';
const PERSPECTIVE_PX = 1900;
/** Доля ширины страницы, после которой отпущенный лист дозакрывается сам. */
const COMMIT_PROGRESS = 0.3;
/** Пока палец не сдвинулся на столько, это ещё не перетаскивание, а клик. */
const DRAG_ACTIVATE_PX = 4;

/**
 * Одна сторона листа: либо страница, либо пустота (за обложкой и за задней
 * обложкой листа нет — там виден только фон сцены).
 */
export type FlipSlot =
  { readonly kind: 'empty' } | { readonly kind: 'leaf'; readonly content: ReactNode };

type Direction = 'forward' | 'backward';

interface FlipState {
  readonly direction: Direction;
  /**
   * Разворот, от которого начался поворот. Раскладка во время анимации
   * считается по нему, а не по `viewIndex`: индекс снаружи меняется сразу,
   * чтобы подпись и озвучка не отставали от жеста на длину анимации.
   */
  readonly from: number;
  readonly progress: number;
  readonly animated: boolean;
}

export interface SpreadFlipViewerProps {
  /**
   * Стороны листов подряд, по две на разворот: пустая сторона, обложка,
   * левая и правая половины каждого разворота, задняя обложка, пустая сторона.
   */
  readonly slots: readonly FlipSlot[];
  readonly leafWidthPx: number;
  readonly leafHeightPx: number;
  /** Номер открытого разворота: `slots[viewIndex * 2]` и следующий за ним. */
  readonly viewIndex: number;
  readonly onViewChange: (next: number) => void;
  readonly reducedMotion: boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Разворот книги с настоящим перелистыванием.
 *
 * Видны две страницы сразу, корешок посередине; лист поворачивается вокруг
 * него — потяните правую страницу вперёд, левую назад. На обратной стороне
 * листа видна следующая страница, поэтому поворот выглядит как у бумажной
 * книги, а не как смена слайда.
 *
 * Номер разворота живёт снаружи: читалке он нужен для подписи, ползунка
 * и озвучки. Компонент только показывает поворот от прежнего значения
 * к новому и сообщает о перелистывании жестом через `onViewChange`.
 */
export function SpreadFlipViewer({
  slots,
  leafWidthPx,
  leafHeightPx,
  viewIndex,
  onViewChange,
  reducedMotion,
}: SpreadFlipViewerProps): JSX.Element {
  const bookWidthPx = leafWidthPx * 2;
  const viewCount = Math.max(1, Math.ceil(slots.length / 2));

  const [flip, setFlip] = useState<FlipState | null>(null);
  const [animating, setAnimating] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  const surfaceRef = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<number | null>(null);
  const previousView = useRef(viewIndex);
  /** Перелистывание жестом само меняет индекс — реагировать на него не нужно. */
  const committedByDrag = useRef(false);
  const drag = useRef<{
    pointerId: number;
    direction: Direction;
    startX: number;
    span: number;
    active: boolean;
    progress: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = surfaceRef.current;
    if (!element) return;
    const measure = (): void =>
      setViewport({ width: element.clientWidth, height: element.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
    },
    [],
  );

  const scale =
    viewport.width > 0 && viewport.height > 0
      ? Math.min(
          (viewport.width - FIT_PADDING_PX * 2) / bookWidthPx,
          (viewport.height - FIT_PADDING_PX * 2) / leafHeightPx,
          1,
        )
      : 1;

  /** Довести лист до конца (`1`) или вернуть на место (`0`) и убрать анимацию. */
  const settle = useCallback((target: 0 | 1) => {
    if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
    setAnimating(true);
    setFlip((current) => (current ? { ...current, progress: target, animated: true } : null));
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = null;
      setFlip(null);
      setAnimating(false);
    }, FLIP_DURATION_MS);
  }, []);

  // Индекс сменился снаружи — кнопкой, клавишей или ползунком.
  useEffect(() => {
    const previous = previousView.current;
    previousView.current = viewIndex;
    if (previous === viewIndex) return;
    if (committedByDrag.current) {
      committedByDrag.current = false;
      return;
    }
    // Прыжок ползунком через полкниги листать нечем — показываем сразу.
    if (reducedMotion || Math.abs(viewIndex - previous) !== 1) {
      if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
      settleTimer.current = null;
      setFlip(null);
      setAnimating(false);
      return;
    }
    setFlip({
      direction: viewIndex > previous ? 'forward' : 'backward',
      from: previous,
      progress: 0,
      animated: false,
    });
  }, [reducedMotion, viewIndex]);

  /**
   * Два кадра до старта: браузер должен успеть отрисовать лист в исходном
   * положении, иначе он окажется повёрнутым сразу, без анимации.
   */
  useEffect(() => {
    if (!flip || flip.animated || dragging) return;
    let raf = 0;
    const frame = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => settle(1));
    });
    return () => {
      cancelAnimationFrame(frame);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [dragging, flip, settle]);

  const canForward = viewIndex < viewCount - 1;
  const canBackward = viewIndex > 0;

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (reducedMotion || animating || flip) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const bounds = surfaceRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const direction: Direction =
        event.clientX >= bounds.left + bounds.width / 2 ? 'forward' : 'backward';
      if (direction === 'forward' && !canForward) return;
      if (direction === 'backward' && !canBackward) return;
      drag.current = {
        pointerId: event.pointerId,
        direction,
        startX: event.clientX,
        span: Math.max(1, leafWidthPx * scale),
        active: false,
        progress: 0,
      };
    },
    [animating, canBackward, canForward, flip, leafWidthPx, reducedMotion, scale],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      if (!current || current.pointerId !== event.pointerId) return;
      const dx = event.clientX - current.startX;
      if (!current.active) {
        if (Math.abs(dx) < DRAG_ACTIVATE_PX) return;
        current.active = true;
        setDragging(true);
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Захват указателя — удобство, а не обязательное условие жеста.
        }
      }
      const travelled = current.direction === 'forward' ? -dx : dx;
      current.progress = clamp(travelled / current.span, 0, 1);
      setFlip({
        direction: current.direction,
        from: viewIndex,
        progress: current.progress,
        animated: false,
      });
    },
    [viewIndex],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const current = drag.current;
      if (!current || current.pointerId !== event.pointerId) return;
      drag.current = null;
      if (!current.active) return;
      setDragging(false);
      if (current.progress >= COMMIT_PROGRESS) {
        committedByDrag.current = true;
        onViewChange(viewIndex + (current.direction === 'forward' ? 1 : -1));
        settle(1);
      } else {
        settle(0);
      }
    },
    [onViewChange, settle, viewIndex],
  );

  /**
   * Каждая сторона листа привязана к своему номеру в `slots` и остаётся одним
   * и тем же узлом дерева, меняя только роль и стиль. Из-за этого React
   * переставляет её, а не пересоздаёт: видео на развороте не прерывается,
   * когда страница уезжает на поворачиваемый лист и обратно.
   *
   * Роли идут по возрастанию номера — так порядок детей не меняется, и React
   * ограничивается добавлением и удалением по краям.
   */
  const left = (flip?.from ?? viewIndex) * 2;
  const angle = flip ? (flip.direction === 'forward' ? -1 : 1) * flip.progress * 180 : 0;
  const faces: readonly { readonly index: number; readonly role: FaceRole }[] = flip
    ? flip.direction === 'forward'
      ? [
          { index: left, role: 'left' },
          { index: left + 1, role: 'front' },
          { index: left + 2, role: 'back' },
          { index: left + 3, role: 'right' },
        ]
      : [
          { index: left - 2, role: 'left' },
          { index: left - 1, role: 'back' },
          { index: left, role: 'front' },
          { index: left + 1, role: 'right' },
        ]
    : [
        { index: left, role: 'left' },
        { index: left + 1, role: 'right' },
      ];

  const shade = flip ? Math.sin(flip.progress * Math.PI) : 0;
  const filled = (role: FaceRole): boolean =>
    faces.some((face) => face.role === role && slots[face.index]?.kind === 'leaf');
  const leftFilled = filled('left');
  const rightFilled = filled('right');

  // Бумажный лист с тенью — только под теми сторонами, где есть страница.
  let sheetLeft = 0;
  let sheetWidth = 0;
  if (leftFilled && rightFilled) sheetWidth = bookWidthPx;
  else if (rightFilled) {
    sheetLeft = leafWidthPx;
    sheetWidth = leafWidthPx;
  } else if (leftFilled) sheetWidth = leafWidthPx;

  return (
    <div
      ref={surfaceRef}
      className="kitup-reader-book"
      /** Пока лист в движении, страницы под ним ещё не те, что будут в конце. */
      data-flipping={flip ? 'true' : undefined}
      style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDragStart={(event) => event.preventDefault()}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: bookWidthPx * scale,
          height: leafHeightPx * scale,
          /*
           * A cover alone stays on its half of the open book, where the page turn starts and
           * ends. Centring it at rest made the book jump sideways the moment a corner was
           * dragged, and back when it settled (owner, on a phone).
           */
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: bookWidthPx,
            height: leafHeightPx,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            transformStyle: 'preserve-3d',
            perspective: `${PERSPECTIVE_PX}px`,
            perspectiveOrigin: '50% 50%',
          }}
        >
          {sheetWidth > 0 ? (
            <div
              aria-hidden="true"
              className="kitup-reader-sheet"
              style={{ left: sheetLeft, width: sheetWidth, height: leafHeightPx }}
            />
          ) : null}

          {leftFilled && rightFilled ? (
            <div
              aria-hidden="true"
              className="kitup-reader-spine"
              style={{ left: leafWidthPx - 26, height: leafHeightPx }}
            />
          ) : null}

          {faces.map(({ index, role }) => {
            const slot = slots[index];
            if (slot?.kind !== 'leaf') return null;
            const turning = role === 'front' || role === 'back';
            return (
              <div
                key={index}
                className={turning ? 'kitup-reader-leaf-face' : undefined}
                style={{
                  position: 'absolute',
                  top: 0,
                  width: leafWidthPx,
                  height: leafHeightPx,
                  overflow: 'hidden',
                  ...faceGeometry(role, flip?.direction, angle, leafWidthPx),
                  ...(turning && animating
                    ? { transition: `transform ${FLIP_DURATION_MS}ms ${FLIP_EASING}` }
                    : null),
                }}
              >
                {slot.content}
                {turning ? (
                  <div
                    aria-hidden="true"
                    className="kitup-reader-leaf-shade"
                    style={{ background: foldShade(role, flip?.direction, shade) }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type FaceRole = 'left' | 'right' | 'front' | 'back';

/**
 * Положение стороны листа.
 *
 * Поворачиваемые стороны висят на одной петле — на корешке, — поэтому обе
 * считают поворот от него: вперёд лист откидывается от левого края правой
 * страницы, назад — от правого края левой.
 *
 * Изнанке нужен ещё сдвиг на страницу. Без него `rotateY(180°)` вокруг петли
 * уводил бы её на противоположную половину разворота: обе стороны листа должны
 * лежать друг на друге, а не по разные стороны корешка.
 */
function faceGeometry(
  role: FaceRole,
  direction: 'forward' | 'backward' | undefined,
  angle: number,
  leafWidthPx: number,
): CSSProperties {
  if (role === 'left') return { left: 0, zIndex: 1 };
  if (role === 'right') return { left: leafWidthPx, zIndex: 1 };

  const forward = direction === 'forward';
  const shift = forward ? -leafWidthPx : leafWidthPx;

  return {
    left: forward ? leafWidthPx : 0,
    zIndex: 5,
    transformOrigin: forward ? 'left center' : 'right center',
    transform:
      role === 'front'
        ? `rotateY(${angle}deg)`
        : `rotateY(${angle + 180}deg) translateX(${shift}px)`,
  };
}

/** Тень набегает к середине поворота и сходит к его концу. */
function foldShade(
  role: FaceRole,
  direction: 'forward' | 'backward' | undefined,
  shade: number,
): string {
  const forward = direction === 'forward';
  const toward = role === 'front' ? (forward ? 'left' : 'right') : forward ? 'right' : 'left';
  const depth = (role === 'front' ? 0.35 : 0.28) * shade;
  return `linear-gradient(to ${toward}, rgba(0,0,0,0) 55%, rgba(0,0,0,${depth}))`;
}
